import { FC, useMemo } from "react";
import { Point, YAxisOptions } from "highcharts";
import { grey } from "@mui/material/colors";
import numeral from "numeral";
import { HighchartsChartDataType } from "@/storybook/c8r-highcharts/common/utils/types/common";
import { MetricsKey } from "@/components/pages/common/resources-ag-grid/utils/metrics/metricsTypes";
import {
  colorsByMetricType,
  metricsUnit,
} from "@/components/pages/common/resources-ag-grid/utils/metrics/metricsConstants";
import { InlineHighcharts } from "@/storybook/c8r-highcharts/inline-highcharts/InlineHighcharts";
import { convertSecondsToUnits } from "@/components/utils/helpers/convertSecondsToUnits";

interface ResourceMetricsChartProps {
  metricKey: MetricsKey;
  chartData: HighchartsChartDataType;
  pointWidth: number;
}

export const ResourceMetricsChart: FC<ResourceMetricsChartProps> = ({
  chartData,
  metricKey,
  pointWidth,
}) => {
  const tooltipMarkupGenerator = useMemo(
    () => getTooltipValueFormatter(metricKey, chartData),
    [metricKey, chartData],
  );

  const plotOptions = useMemo(
    () => getPlotOptions(metricKey, pointWidth),
    [metricKey, pointWidth],
  );

  return (
    <InlineHighcharts
      yAxis={yAxis}
      boxHeight={40}
      chartData={chartData}
      pointWidth={pointWidth}
      plotOptions={plotOptions}
      tooltipMarkupGenerator={tooltipMarkupGenerator}
    />
  );
};

const yAxis: YAxisOptions = {
  lineColor: grey[300],
  lineWidth: 1,
  gridLineDashStyle: "Dash",
};

export const getTooltipValueFormatter =
  (metricKey: MetricsKey, chartData: HighchartsChartDataType) =>
  (point: Point) => {
    const color = colorsByMetricType.get(metricKey)?.["700"];
    const unit = metricsUnit[metricKey];

    const index = point.index ?? 0;
    const y = chartData?.series?.at?.(0)?.data?.[index]?.y ?? null;
    let value = y === null ? "N/A" : numeral(y).format("0,0.[00]");
    let valueUnit = y !== null && !isNaN(y) ? metricsUnit[metricKey] : "";

    if (unit?.toLowerCase() === "seconds") {
      const converted = convertSecondsToUnits(y ?? 0);

      value = `${converted.value.toFixed(1)}`;
      valueUnit = converted.label;
    }

    return `
    <div style="font-size: 12px; color: #666; margin-bottom: 8px;">${point.category} (UTC)</div>  
      <div style="display: flex; flex-direction: column; gap: 8px;">
        <div style="display: flex; justify-content: space-between; align-items: center;">
            <span style="display: flex; align-items: center; font-size: 10px; margin-right: 16px;">
                <span style="font-weight: 500;  border-left: 3px solid ${color}; padding-left: 8px;">
                    ${point?.series?.name}
                </span>
            </span>

          <span style="font-weight: bold; font-size: 10px; "> ${value} ${valueUnit}</span>
        </div>
      </div>
      `;
  };

const getPlotOptions = (metricKey: MetricsKey, pointWidth: number) => {
  return {
    column: {
      pointWidth: pointWidth,
      pointPadding: 0.5,
      groupPadding: 0,
      borderWidth: 0,
      color: colorsByMetricType.get(metricKey)?.["100"],
      minPointLength: 2,
      states: {
        hover: {
          enabled: true,
          color: colorsByMetricType.get(metricKey)?.["700"],
        },
      },
    },
    series: {
      animation: false,
      states: { inactive: { enabled: false } },
    },
  };
};
